<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay">
      <div class="modal-content dashboard-project-create-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ s__('Project|New Project') }}</h3>
          <button type="button" class="modal-close" aria-label="Close" @click="$emit('close')">
            ×
          </button>
        </div>

        <div class="modal-body">
          <div v-if="step === 'type'" class="form-container">
            <div class="step-header">
              <h4 class="step-title">{{ s__('Project|Choose project type') }}</h4>
              <p class="step-indicator">{{ stepIndicator }}</p>
            </div>
            <div class="project-type-options">
              <button
                type="button"
                class="project-type-option"
                :class="{
                  active: projectType === 'personal',
                  'project-type-option--blocked': !canCreatePersonalProject,
                }"
                :disabled="submitting"
                :aria-disabled="!canCreatePersonalProject || undefined"
                @click="onProjectTypeOptionClick('personal')"
              >
                <strong>{{ s__('Project|Private project') }}</strong>
                <span>{{ s__('Project|Only you can access it.') }}</span>
                <div
                  v-if="!canCreatePersonalProject"
                  class="project-type-hint project-type-hint--icon-only"
                >
                  <LockIcon />
                </div>
              </button>
              <button
                type="button"
                class="project-type-option"
                :class="{
                  active: projectType === 'team',
                  'project-type-option--blocked': !canCreateTeamProject,
                }"
                :disabled="submitting"
                :aria-disabled="!canCreateTeamProject || undefined"
                @click="onProjectTypeOptionClick('team')"
              >
                <strong>{{ s__('Project|Team project') }}</strong>
                <span>{{ s__('Project|Collaborate with other users.') }}</span>
                <div
                  v-if="!canCreateTeamProject"
                  class="project-type-hint project-type-hint--icon-only"
                >
                  <LockIcon />
                </div>
              </button>
            </div>

            <div
              v-if="anyOwnershipLimitReached"
              ref="ownershipLimitInfoEl"
              class="project-ownership-limit-info"
              :class="{ 'project-ownership-limit-info--emphasize': ownershipInfoEmphasize }"
              @animationend.self="onOwnershipInfoEmphasizeAnimationEnd"
            >
              <p v-if="!canCreatePersonalProject" class="project-type-hint-line">
                {{ s__('Project|Free accounts can own only one unarchived personal project.') }}
              </p>
              <p v-if="!canCreateTeamProject" class="project-type-hint-line">
                {{ s__('Project|Free accounts can own only one unarchived team project.') }}
              </p>
              <p class="subscription-hint-para">
                {{
                  s__(
                    "Project|We're planning to offer more personal and team projects as part of a paid plan later on. For now, if you need several workspaces, we're happy to help — write us on Telegram"
                  )
                }}
                <span class="copyable-inline copyable-inline--popover">
                  <code class="contact-mono">{{ telegramSupportHandle }}</code>
                  <span
                    v-if="copyFeedbackKind === 'telegram'"
                    :key="copyFeedbackTick"
                    class="copy-contact-feedback-bubble"
                    @animationend="onCopyFeedbackAnimationEnd"
                  >
                    {{ copiedLabel }}
                  </span>
                  <button
                    type="button"
                    class="copy-contact-btn"
                    :aria-label="copyTelegramLabel"
                    @click.stop="copyContact('telegram')"
                  >
                    <CopyIcon size="14" color="#666" />
                  </button>
                </span>
                {{ s__('Project| or at') }}
                <span class="copyable-inline copyable-inline--popover">
                  <code class="contact-mono">{{ supportEmail }}</code>
                  <span
                    v-if="copyFeedbackKind === 'email'"
                    :key="copyFeedbackTick"
                    class="copy-contact-feedback-bubble"
                    @animationend="onCopyFeedbackAnimationEnd"
                  >
                    {{ copiedLabel }}
                  </span>
                  <button
                    type="button"
                    class="copy-contact-btn"
                    :aria-label="copyEmailLabel"
                    @click.stop="copyContact('email')"
                  >
                    <CopyIcon size="14" color="#666" />
                  </button>
                </span>
                .
              </p>
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="btn-sm btn-secondary"
                :disabled="submitting"
                @click="$emit('close')"
              >
                {{ s__('Project|Cancel') }}
              </button>
              <button
                type="button"
                class="btn-sm btn-primary"
                :disabled="submitting || !projectType"
                @click="proceedFromType"
              >
                {{ __('Next') }}
              </button>
            </div>
          </div>

          <div v-else-if="step === 'team-info' && canShowTeamInfoStep" class="form-container">
            <div class="step-header">
              <h4 class="step-title">{{ s__('Project|Create team project') }}</h4>
              <p class="step-indicator">{{ stepIndicator }}</p>
            </div>
            <div class="info-banner">
              <p>
                {{
                  s__(
                    'Project|Alternative: add the bot to your Telegram group. Members who join via the bot/link will be added to the project automatically.'
                  )
                }}
              </p>
            </div>
            <div class="form-actions">
              <button
                type="button"
                class="btn-sm btn-secondary"
                :disabled="submitting"
                @click="goBack"
              >
                {{ __('Back') }}
              </button>
              <button
                type="button"
                class="btn-sm btn-primary"
                :disabled="submitting"
                @click="step = 'details'"
              >
                {{ s__('Project|Create via UI') }}
              </button>
            </div>
          </div>

          <div v-else-if="step === 'details'" class="form-container">
            <div class="step-header">
              <h4 class="step-title">{{ s__('Project|Project details') }}</h4>
              <p class="step-indicator">{{ stepIndicator }}</p>
            </div>
            <div class="form-row">
              <label>{{ s__('Project|Title') }} *</label>
              <input
                v-model="title"
                type="text"
                :placeholder="s__('Project|Enter project title')"
                :disabled="submitting"
              />
            </div>

            <div class="form-row">
              <label>{{ s__('Project|Description') }}</label>
              <textarea
                v-model="description"
                :placeholder="s__('Project|Enter project description (optional)')"
                rows="4"
                :disabled="submitting"
              ></textarea>
            </div>

            <div class="form-row">
              <label>{{ s__('Project|Code') }}</label>
              <input
                v-model="code"
                type="text"
                :placeholder="s__('Project|Optional short code, e.g. TRCK')"
                :disabled="submitting"
              />
              <div class="help-text">
                {{
                  s__(
                    'Project|Optional. 3-12 chars, letters/digits/_/-. Must include at least one letter. Once set, code cannot be changed.'
                  )
                }}
              </div>
              <div v-if="normalizedCode && !isCodeValid" class="error">
                {{
                  s__(
                    "Project|Invalid code format. Use 3-12 chars: latin letters, digits, '_' or '-', with at least one letter."
                  )
                }}
              </div>
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="btn-sm btn-secondary"
                :disabled="submitting"
                @click="goBack"
              >
                {{ __('Back') }}
              </button>
              <button
                type="button"
                class="btn-sm btn-primary"
                :disabled="submitting || !canProceedFromDetails"
                @click="step = 'workflow'"
              >
                {{ __('Next') }}
              </button>
            </div>
          </div>

          <div v-else class="form-container">
            <div class="step-header">
              <h4 class="step-title">{{ s__('Project|Choose workflow') }}</h4>
              <p class="step-indicator">{{ stepIndicator }}</p>
            </div>
            <div class="form-row">
              <label>{{ s__('Project|Workflow Type') }} *</label>
              <WorkflowTypeSelector
                v-model="workflowType"
                :disabled="submitting"
                :translation-namespace="workflowTranslationNamespace"
              />
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="btn-sm btn-secondary"
                :disabled="submitting"
                @click="goBack"
              >
                {{ __('Back') }}
              </button>
              <button
                type="button"
                class="btn-sm btn-primary"
                :disabled="submitting || !canProceedFromDetails"
                @click="createProject"
              >
                {{ submitting ? s__('Project|Creating...') : createButtonLabel }}
              </button>
            </div>
          </div>

          <div v-if="error" class="error" style="margin-top: 12px">{{ error }}</div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __, s__ } from '@/locale'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import LockIcon from '../common/icons/LockIcon.vue'
import CopyIcon from '../common/icons/CopyIcon.vue'
import WorkflowTypeSelector from '../common/WorkflowTypeSelector.vue'

const telegramSupportHandle = '@ArtemkaGro'
const supportEmail = 'support@tracktor.space'
const copyTelegramLabel = s__('Project|Copy Telegram username')
const copyEmailLabel = s__('Project|Copy support email')

const props = defineProps({
  visible: { type: Boolean, default: false },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
  projects: { type: Array, default: () => [] },
  premiumActive: { type: Boolean, default: false },
  hasTelegramUserId: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'created'])

const submitting = ref(false)
const error = ref('')
const step = ref('type')
const projectType = ref(null)
const title = ref('')
const description = ref('')
const code = ref('')
const workflowType = ref('standard')
const copyFeedbackKind = ref(null)
const copyFeedbackTick = ref(0)
const ownershipLimitInfoEl = ref(null)
const ownershipInfoEmphasize = ref(false)

const CREATE_PROJECT = gql`
  mutation CreateProject(
    $title: String!
    $description: String
    $workflowType: String!
    $code: String
  ) {
    createProject(
      title: $title
      description: $description
      workflowType: $workflowType
      code: $code
    ) {
      project {
        id
        slug
        title
      }
      errors
    }
  }
`

const { mutate: createProjectMutation } = useMutation(CREATE_PROJECT)

const CREATE_PERSONAL_PROJECT = gql`
  mutation CreatePersonalProject(
    $title: String
    $description: String
    $workflowType: String!
    $code: String
  ) {
    createPersonalProject(
      title: $title
      description: $description
      workflowType: $workflowType
      code: $code
    ) {
      project {
        id
        slug
        title
      }
      errors
    }
  }
`

const { mutate: createPersonalProjectMutation } = useMutation(CREATE_PERSONAL_PROJECT)

const CODE_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9_-]{3,12}$/
const normalizedCode = computed(() => code.value.trim())
const isCodeValid = computed(
  () => normalizedCode.value.length === 0 || CODE_PATTERN.test(normalizedCode.value)
)
const canProceedFromDetails = computed(() => title.value.trim().length > 0 && isCodeValid.value)
const unarchivedOwnedPersonalProjectsCount = computed(
  () =>
    props.projects.filter(
      project =>
        project.userRole === 'owner' && project.isPersonal === true && project.archived !== true
    ).length
)
const unarchivedOwnedTeamProjectsCount = computed(
  () =>
    props.projects.filter(
      project =>
        project.userRole === 'owner' && project.isPersonal !== true && project.archived !== true
    ).length
)

const canCreatePersonalProject = computed(
  () => props.premiumActive || unarchivedOwnedPersonalProjectsCount.value < 1
)
const canCreateTeamProject = computed(
  () => props.premiumActive || unarchivedOwnedTeamProjectsCount.value < 1
)
const anyOwnershipLimitReached = computed(
  () => !canCreatePersonalProject.value || !canCreateTeamProject.value
)
const canShowTeamInfoStep = computed(() => props.hasTelegramUserId || !!props.fromId)

const stepIndicator = computed(() => {
  if (step.value === 'type') return s__('Project|Step 1 of 3')
  if (step.value === 'details') return s__('Project|Step 2 of 3')
  if (step.value === 'workflow') return s__('Project|Step 3 of 3')
  return s__('Project|Step 1 of 3')
})

const workflowTranslationNamespace = computed(() =>
  projectType.value === 'personal' ? 'PersonalProject' : 'Project'
)

const createButtonLabel = computed(() =>
  projectType.value === 'personal'
    ? s__('PersonalProject|Create Personal Project')
    : s__('Project|Create Project')
)

const copiedLabel = computed(() => s__('Project|Copied!'))

useBodyScrollLock(computed(() => props.visible))

watch(
  () => props.visible,
  isVisible => {
    if (!isVisible) return
    submitting.value = false
    error.value = ''
    step.value = 'type'
    projectType.value = null
    title.value = ''
    description.value = ''
    code.value = ''
    workflowType.value = 'standard'
  }
)

watch([canCreatePersonalProject, canCreateTeamProject], ([canCreatePersonal, canCreateTeam]) => {
  if (projectType.value === 'personal' && !canCreatePersonal) {
    projectType.value = null
  }
  if (projectType.value === 'team' && !canCreateTeam) {
    projectType.value = null
  }
})

function onCopyFeedbackAnimationEnd(event) {
  if (event.animationName !== 'copy-contact-feedback-pop') return
  copyFeedbackKind.value = null
}

function onOwnershipInfoEmphasizeAnimationEnd() {
  ownershipInfoEmphasize.value = false
}

function emphasizeOwnershipLimitInfo() {
  if (!anyOwnershipLimitReached.value) return
  ownershipInfoEmphasize.value = false
  nextTick(() => {
    ownershipInfoEmphasize.value = true
    const el = ownershipLimitInfoEl.value
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}

function onProjectTypeOptionClick(kind) {
  if (submitting.value) return
  if (kind === 'personal' && !canCreatePersonalProject.value) {
    emphasizeOwnershipLimitInfo()
    return
  }
  if (kind === 'team' && !canCreateTeamProject.value) {
    emphasizeOwnershipLimitInfo()
    return
  }
  projectType.value = kind
}

async function copyContact(kind) {
  const text = kind === 'telegram' ? telegramSupportHandle : supportEmail
  if (!text) return
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    copyFeedbackKind.value = kind
    copyFeedbackTick.value += 1
  } catch {
    // Clipboard may be denied in some contexts
  }
}

function buildBaseUrl(path) {
  let url = path
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

async function createProject() {
  if (submitting.value || !canProceedFromDetails.value) return

  submitting.value = true
  error.value = ''

  try {
    if (projectType.value === 'personal') {
      const { data } = await createPersonalProjectMutation({
        title: title.value.trim(),
        description: description.value.trim() || null,
        workflowType: workflowType.value,
        code: normalizedCode.value || null,
      })

      if (data?.createPersonalProject?.errors?.length) {
        error.value = data.createPersonalProject.errors.join(', ')
        submitting.value = false
        return
      }

      emit('created')
      emit('close')
      return
    }

    const { data } = await createProjectMutation({
      title: title.value.trim(),
      description: description.value.trim() || null,
      workflowType: workflowType.value,
      code: normalizedCode.value || null,
    })

    if (data?.createProject?.errors?.length) {
      error.value = data.createProject.errors.join(', ')
      submitting.value = false
      return
    }

    const projectSlug = data?.createProject?.project?.slug || data?.createProject?.project?.id
    if (projectSlug) {
      window.location.href = buildBaseUrl(`/projects/${projectSlug}/dashboard`)
      return
    }

    emit('created')
    emit('close')
  } catch (e) {
    error.value = e.message || s__('Project|Failed to create project')
    submitting.value = false
  }
}

function proceedFromType() {
  if (!projectType.value) return
  if (projectType.value === 'personal' && !canCreatePersonalProject.value) return
  if (projectType.value === 'team' && !canCreateTeamProject.value) return
  if (projectType.value === 'team' && canShowTeamInfoStep.value) {
    step.value = 'team-info'
    return
  }
  step.value = 'details'
}

function goBack() {
  if (step.value === 'workflow') {
    step.value = 'details'
    return
  }
  if (step.value === 'details') {
    step.value = projectType.value === 'team' && canShowTeamInfoStep.value ? 'team-info' : 'type'
    return
  }
  if (step.value === 'team-info') {
    step.value = 'type'
  }
}
</script>

<style src="../../styles/forms.css"></style>

<style scoped>
.dashboard-project-create-modal {
  max-width: 720px;
}

.step-indicator {
  margin: 0;
  color: #666;
  font-size: 14px;
  margin-left: auto;
  text-align: right;
}

.step-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin-bottom: 14px;
}

.step-title {
  margin: 0;
  font-size: 18px;
}

.project-type-options {
  display: grid;
  gap: 10px;
  margin-bottom: 0;
}

.project-type-option {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  display: grid;
  gap: 4px;
  text-align: center;
  justify-items: center;
  color: #333;
}

.project-type-option strong,
.project-type-option span {
  display: block;
  text-align: center;
  width: 100%;
}

.project-type-option:hover:not(:disabled):not(.project-type-option--blocked) {
  border-color: #2aabee;
  background: #f8fbff;
}

.project-type-option.active {
  border-color: #2aabee;
  background: #edf7ff;
}

.project-type-option:disabled,
.project-type-option--blocked {
  opacity: 0.65;
  cursor: not-allowed;
}

.project-type-option--blocked:hover:not(:disabled) {
  border-color: #d1d5db;
  background: #fff;
}

.project-type-hint {
  margin-top: 2px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #666;
  text-align: left;
  width: 100%;
}

.project-type-hint--icon-only {
  justify-content: center;
  margin-top: 6px;
}

.project-ownership-limit-info {
  margin-top: 14px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  text-align: left;
  scroll-margin-top: 12px;
}

.project-ownership-limit-info--emphasize {
  animation: ownership-limit-info-emphasize 1.35s ease forwards;
}

@keyframes ownership-limit-info-emphasize {
  0%,
  100% {
    border-color: #e5e7eb;
    box-shadow: none;
    background: #fafafa;
  }
  12%,
  55% {
    border-color: #2aabee;
    box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.22);
    background: #f0f9ff;
  }
}

.project-ownership-limit-info .project-type-hint-line {
  margin: 0 0 8px;
  color: #666;
}

.project-ownership-limit-info .project-type-hint-line:last-of-type {
  margin-bottom: 12px;
}

.project-ownership-limit-info .subscription-hint-para {
  margin-top: 0;
}

.project-type-hint-line {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
}

.subscription-hint-para {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #666;
}

.form-row {
  margin-bottom: 20px;
}

.form-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #495057;
}

.form-row input[type='text'],
.form-row textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-row input[type='text']:focus,
.form-row textarea:focus {
  outline: none;
  border-color: #1d6bf3;
  box-shadow: 0 0 0 3px rgba(29, 107, 243, 0.1);
}

.form-row input[type='text']:disabled,
.form-row textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
}

.form-actions button {
  min-height: 44px;
  padding: 12px 22px;
  font-size: 15px;
}

.info-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  color: #1e3a8a;
}

.info-banner p {
  margin: 0;
}
</style>
